import { useTheme } from '@Theme';
import { css } from 'aphrodite';
import { FC, useMemo, useState } from 'react';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { useAccountProfileQuery } from '../../../../queries/AccountProfile/useAccountProfileQuery';
import { AIAssistantDialog } from '../../../components-v2/ai/AIAssistantDialog';
import { AiButton } from '../../../components-v2/AiButton';
import { Column } from '../../../components-v2/PageLayout/Layout';
import { CloseButton } from '../../CloseButton';
import { SparklesIcon } from '../../svgs/icons/SparklesIcon';
import { DashboardCard, DashboardCardHeaderLabel } from '../DashboardCard';
import { CustomContentGraphic } from './CustomContentGraphic';
import { styleSheet } from './styles';
import { useFeatureCard } from './useFeatureCard';
import { AIConversationType } from '@ViewModels';

export const DashboardCustomContentCard: FC = () => {
	const userSession = useUserSession();
	const { colors } = useTheme();
	const { showCard, onClose } = useFeatureCard({ cardName: 'custom-content-preferences-card', userRole: 'admin' });

	const [aiAssistantOpen, setAiAssistantOpen] = useState<boolean>(false);

	const accountProfileQuery = useAccountProfileQuery({ impersonationContext: userSession?.impersonationContext });

	// Check if business description is already set up
	const hasBusinessDescription = useMemo(() => {
		const businessDescription = accountProfileQuery.data?.businessDescription;
		return !!(businessDescription && businessDescription.trim().length > 0);
	}, [accountProfileQuery.data?.businessDescription]);

	const shouldShowCard =
		showCard && userSession.account.features?.contentGeneration?.enabled && !hasBusinessDescription;

	const handleClick = () => {
		setAiAssistantOpen(true);
	};

	const initialMessage = useMemo(() => {
		const userName = userSession?.user?.firstName || 'there';
		const accountName = userSession?.account?.companyName || 'your account';
		return `Hi ${userName}! I'm here to help set up your Custom Content Preferences for "${accountName}."<br /><br />Give me a second while I check your current settings.`;
	}, [userSession?.user?.firstName, userSession?.account?.companyName]);

	return aiAssistantOpen || shouldShowCard ? (
		<DashboardCard className={css(styleSheet.card)}>
			<div className={css(styleSheet.cardHeader)}>
				<DashboardCardHeaderLabel styleDeclaration={styleSheet.headerContent}>
					<SparklesIcon color={colors.primary.main} height={27} width={27} />
					<strong>New!</strong>
					<span className={css(styleSheet.header)}>Make any campaign unique to your business with AI</span>
				</DashboardCardHeaderLabel>
				<CloseButton onClick={onClose} />
			</div>
			<div className={css(styleSheet.divider)} />
			<article className={css(styleSheet.content)}>
				<Column styles={[styleSheet.cardColumn, styleSheet.leftColumn]}>
					<p className={css(styleSheet.description)}>Answer a few quick questions to get started today!</p>
					<AiButton title='Get Started Now' onClick={handleClick} />
				</Column>
				<Column styles={[styleSheet.cardColumn]}>
					<CustomContentGraphic className={css(styleSheet.graphic)} />
				</Column>
			</article>
			<AIAssistantDialog
				open={aiAssistantOpen}
				onOpenChange={setAiAssistantOpen}
				assistantName='AccountOnboardAgent'
				conversationType={AIConversationType.AccountOnboarding}
				title='Account Onboarding Assistant'
				initialMessage={initialMessage}
				placeholder='Send a message...'
			/>
		</DashboardCard>
	) : null;
};
